import { useState, useRef, useEffect } from 'react';
import { useConversation, type DateRangePreset } from '../../contexts/ConversationContext';

const PRESETS: { value: DateRangePreset; label: string; shortLabel: string }[] = [
  { value: 'all', label: 'All Time', shortLabel: 'All' },
  { value: 'today', label: 'Today', shortLabel: 'Today' },
  { value: '7d', label: 'Last 7 Days', shortLabel: '7D' },
  { value: '30d', label: 'Last 30 Days', shortLabel: '30D' },
  { value: '90d', label: 'Last 90 Days', shortLabel: '90D' },
];

export default function DateRangePicker() {
  const { dateRange, setDateRangePreset, setDateRange } = useConversation();
  const [isOpen, setIsOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync custom inputs with current range
  useEffect(() => {
    if (dateRange.preset === 'custom') {
      setCustomStart(dateRange.startDate || '');
      setCustomEnd(dateRange.endDate || '');
    }
  }, [dateRange]);

  const handlePresetSelect = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    setIsOpen(false);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      setDateRange({
        preset: 'custom',
        startDate: customStart,
        endDate: customEnd,
      });
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (dateRange.preset === 'custom' && dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = new Date(dateRange.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${start} - ${end}`;
    }
    return PRESETS.find((p) => p.value === dateRange.preset)?.label || 'All Time';
  };

  const getShortDisplayText = () => {
    if (dateRange.preset === 'custom') {
      return 'Custom';
    }
    return PRESETS.find((p) => p.value === dateRange.preset)?.shortLabel || 'All';
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white transition-colors"
      >
        {/* Calendar icon */}
        <svg
          className="w-4 h-4 text-white/70"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M3 10h18" />
          <path d="M8 2v4" />
          <path d="M16 2v4" />
        </svg>
        {/* Full text on desktop, short on mobile */}
        <span className="hidden sm:inline text-sm font-light">{getDisplayText()}</span>
        <span className="sm:hidden text-xs font-mono uppercase">{getShortDisplayText()}</span>
        {/* Chevron */}
        <svg
          className={`w-3 h-3 text-white/50 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-grey-medium/20 shadow-elevated animate-fade-in-up" style={{ zIndex: 500 }}>
          {/* Presets */}
          <div className="py-0.5">
            {PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => handlePresetSelect(preset.value)}
                className={`w-full px-3 py-2 text-left text-xs font-light transition-colors flex items-center justify-between ${
                  dateRange.preset === preset.value
                    ? 'bg-mint/10 text-black'
                    : 'text-black hover:bg-grey-light'
                }`}
              >
                <span>{preset.label}</span>
                {dateRange.preset === preset.value && (
                  <svg className="w-4 h-4 text-mint" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Custom Range */}
          <div className="border-t border-grey-medium/20 p-3">
            <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
              Custom Range
            </p>
            <div className="flex gap-2 mb-2">
              <div className="flex-1">
                <label className="text-[10px] text-grey-medium mb-0.5 block">From</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-grey-medium/30 focus:border-mint focus:outline-none bg-white"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-grey-medium mb-0.5 block">To</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-grey-medium/30 focus:border-mint focus:outline-none bg-white"
                />
              </div>
            </div>
            <button
              onClick={handleCustomApply}
              disabled={!customStart || !customEnd}
              className="w-full py-1.5 text-xs font-mono uppercase tracking-wider bg-black text-white disabled:bg-grey-medium disabled:cursor-not-allowed hover:bg-black/90 active:scale-[0.98] transition-all"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
